<template>
  <Transition
    name="fade-transition"
    :appear="true"
    :css="false"
    @before-enter="onBeforeEnter"
    @enter="onEnter"
    @before-leave="onBeforeLeave"
    @leave="onLeave"
  >
    <slot />
  </Transition>
</template>

<!-- eslint-disable no-param-reassign -->
<script>

import { defaultDuration } from '@src/components/Transitions/utils/transitionHelpers';

const getComputedStyle = (el) => {
  if (document.defaultView && document.defaultView.getComputedStyle) return document.defaultView.getComputedStyle(el);
  return window.getComputedStyle(el);
};

const computePadding = (style) => {
  return {
    paddingTop: parseFloat(style.paddingTop) || 0,
    paddingBottom: parseFloat(style.paddingBottom) || 0,
  };
};

const computeMargins = (style) => {
  return {
    marginTop: parseFloat(style.marginTop) || 0,
    marginBottom: parseFloat(style.marginBottom) || 0,
  };
};

export default {
  name: 'ExpandTransition',
  props: {
    keep: {
      type: Boolean,
      default: true,
    },
    duration: {
      type: Number,
      default: defaultDuration,
    },
  },
  data() {
    return {
      height: 0,
    };
  },
  computed: {
    animationDuration() {
      return `0.${this.duration}s`;
    },
    styleHeight() {
      return `${this.height}px`;
    },
  },
  methods: {
    onBeforeEnter(el) {
      el.style.opacity = 0;
      el.cachedOverflowStyle = el.style.overflow;
      el.style.overflow = 'hidden';
      el.cachedMaxHeight = el.style.maxHeight;
    },

    async onEnter(el, done) {
      this.height = el.offsetHeight;
      const computedStyle = getComputedStyle(el);

      const computedMargin = computeMargins(computedStyle);
      el.cachedMargin = computedMargin;
      el.style.marginTop = '0px';
      el.style.marginBottom = '0px';
      el.style.maxHeight = `${this.height}px`;

      const computedPadding = computePadding(computedStyle);
      el.cachedPadding = computedPadding;
      el.style.paddingTop = '0px';
      el.style.paddingBottom = '0px';
      el.style.opacity = 1;

      el.classList.remove('sb-expand-expand-animation-out');
      el.classList.add('sb-expand-expand-animation-in');

      el.style.marginTop = computedMargin.marginTop;
      el.style.marginBottom = computedMargin.marginBottom;
      el.style.paddingTop = computedPadding.paddingTop;
      el.style.paddingBottom = computedPadding.paddingBottom;

      done();
    },

    onBeforeLeave(el) {
      el.cachedOverflowStyle = el.style.overflow;
      el.style.overflow = 'hidden';
      el.cachedMaxHeight = el.style.maxHeight;
      el.style.maxHeight = `${el.offsetHeight}px`;
    },

    async onLeave(el, done) {
      el.style.marginTop = '0px';
      el.style.marginBottom = '0px';
      el.style.paddingTop = '0px';
      el.style.paddingBottom = '0px';

      el.classList.remove('sb-expand-expand-animation-in');
      el.classList.add('sb-expand-expand-animation-out');

      // eslint-disable-next-line no-promise-executor-return
      await new Promise((resolve) => setTimeout(resolve, this.duration));

      if (!this.keep) el.remove();

      el.style.maxHeight = el.cachedMaxHeight;
      el.style.overflow = el.cachedOverflowStyle;

      done();
    },
  },
};

</script>

<style lang="scss" scoped>

  .sb-expand-expand-animation-in {
    animation: v-bind(animationDuration) expand-animation-in forwards;
  }

  .sb-expand-expand-animation-out {
    animation: v-bind(animationDuration) expand-animation-out forwards;
  }

  @keyframes expand-animation-in {
    from { height: 0; }
    to { height: v-bind(styleHeight); }
  }

  @keyframes expand-animation-out {
    from { height: v-bind(styleHeight); }
    to { height: 0; }
  }
</style>
